<template>
    <div class="h-100">
        <b-container class="p-0" fluid>
            <b-row align-h="center">
                <b-col md="10">
                    <b-link :to="{ name: 'secure-exports' }">
                        <font-awesome-icon icon="arrow-left" />
                        {{ $t("Back") }}
                    </b-link>
                </b-col>
            </b-row>
            <b-row align-h="center">
                <b-col md="10">
                    <h1 class="mb-2">{{ $t("secure-exports.title") }}</h1>
                </b-col>
            </b-row>

            <b-row align-h="start">
                <b-col md="1" sm="0"></b-col>
                <b-col class="text-center" lg="auto">
                    <div class="date-picker-container shadow px-2 pt-2 round">
                        <v-date-picker
                            ref="datePicker"
                            v-model="dateRange"
                            :locale="$i18n.locale"
                            :max-date="endDate"
                            is-range
                        />
                    </div>
                    <br />
                    <b-button
                        :disabled="conversationsExportStatus && conversationsExportStatus.status === 'pending'"
                        class="font-small-3 mb-50 mx-50"
                        variant="primary"
                        @click="
                            startExport();
                            disableButtonAfterOneMinute();
                        "
                        >{{ $t("secure-exports.generate.generate-csv-file") }}
                    </b-button>

                    <b-button
                        :disabled="!conversationsExportFileUrl || isDisabled"
                        class="font-small-3 mb-50 mx-50"
                        variant="primary"
                        @click="downloadFile"
                    >
                        {{ $t("secure-exports.download.download-csv-file") }}
                    </b-button>

                    <template v-if="!conversationsExportFileUrl">
                        <template v-if="conversationsExportStatus && conversationsExportStatus.status === 'failed'">
                            <b-alert class="p-50 mt-1 text-left d-flex" show variant="warning">
                                <font-awesome-icon class="mr-50" icon="triangle-exclamation" size="lg" />
                                <span>{{ $t("secure-exports.download.not-available") }}</span>
                            </b-alert>
                        </template>
                        <template v-if="conversationsExportStatus && conversationsExportStatus.status === 'success'">
                            <b-alert class="p-50 mt-1 text-left d-flex" show variant="success">
                                <font-awesome-icon class="mr-50" icon="bell" size="lg" />
                                <span>{{ $t("secure-exports.download.download-ready") }}</span>
                            </b-alert>
                        </template>

                        <div
                            v-if="conversationsExportStatus && conversationsExportStatus.status === 'pending'"
                            class="d-flex justify-content-center align-items-baseline mt-1"
                        >
                            <b-spinner
                                :label="$t('secure-exports.download.preparing')"
                                class="mr-50"
                                small
                                variant="primary"
                            />
                            <p class="text-left">{{ $t("secure-exports.download.preparing") }}</p>
                        </div>
                    </template>
                </b-col>
                <b-col class="ml-2" lg="5" md="6">
                    <h4 class="mb-2">{{ $t("secure-exports.description-title") }}</h4>
                    <p class="text-secondary font-small-3">
                        {{ $t("secure-exports.description") }}
                    </p>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
import useConversationExports from "@/composables/useConversationExports";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
import i18n from "@/libs/i18n";
import { transformResponseMessageToErrorMessage } from "@/utils/exports";
import { ref } from "@vue/composition-api";
import { BAlert, BButton, BCalendar, BCol, BContainer, BLink, BRow, BSpinner } from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";

export default {
    components: {
        BContainer,
        BCalendar,
        BButton,
        BSpinner,
        BRow,
        BCol,
        BAlert,
        BLink,
    },
    setup() {
        const toast = useToast();
        const { conversationsExportFileUrl, conversationsExportStatus } = useConversationExports();
        let isDisabled = ref(false);
        // NOTE: I use the normal JS Date object here because the calendar uses that too.
        // Luxon and JS use a different method to turn the object into an ISO string, so this is easier to work with.
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 1);
        const endDate = new Date();

        const dateRange = ref({
            start: startDate,
            end: endDate,
        });

        const disableButtonAfterOneMinute = () => {
            setTimeout(() => {
                isDisabled.value = true;
            }, 60000);
        };

        const startExport = () => {
            conversationsExportFileUrl.value = undefined;
            conversationsExportStatus.value = {
                status: "pending",
                message: undefined,
            };

            axios
                .post("/secure-exports/chats", {
                    startPeriod: dateRange.value.start.toISOString(),
                    endPeriod: dateRange.value.end.toISOString(),
                })
                .catch((error) => {
                    notifyWithError(error.response.data.message);
                    conversationsExportStatus.value = {
                        status: "failed",
                        message: error.response.data.message,
                    };
                });
        };

        const downloadFile = () => {
            return axios
                .get(conversationsExportFileUrl.value, {
                    responseType: "blob",
                })
                .then((downloadedFile) => {
                    const url = window.URL.createObjectURL(new Blob([downloadedFile.data]));

                    // Create an element to serve the data to the browser as download
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute(
                        "download",
                        `secure-export_${dateRange.value.start
                            .toISOString()
                            .replace(/[.:-]/g, "")}-${dateRange.value.end.toISOString().replace(/[.:-]/g, "")}.csv`
                    );
                    document.body.appendChild(link);
                    link.click();
                })
                .catch(() => {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("exports.success.title"),
                            text: i18n.t("secure-exports.generate.error.could-not-download"),
                            icon: "times",
                            variant: "danger",
                        },
                    });
                });
        };

        const notifyWithError = (errorMessage) => {
            toast({
                component: ToastificationContent,
                props: {
                    title: i18n.t("exports.error.title"),
                    text: i18n.t(transformResponseMessageToErrorMessage(errorMessage)),
                    icon: "times",
                    variant: "danger",
                },
            });
        };

        return {
            isDisabled,
            conversationsExportStatus,
            dateRange,
            conversationsExportFileUrl,
            endDate,
            disableButtonAfterOneMinute,
            startExport,
            downloadFile,
            notifyWithError,
        };
    },
};
</script>
